import { Close, CloseRounded } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { APP_LOCAL_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { useAppSelector } from 'app/config/store';

import TruckImplement from 'app/entities/truck-implement/truck-implement';
import { getAllByCompanyIdAndLicensePlate, getEntities } from 'app/entities/truck-implement/truck-implement.reducer';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { ITruckImplement } from 'app/shared/model/truck-implement.model';
import { IUser } from 'app/shared/model/user.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { extractOnlyIds } from 'app/shared/util/functions-util';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC, SMALL_ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import React, { useEffect, useState } from 'react';
import { getSortState, TextFormat, translate } from 'react-jhipster';
import { useDispatch } from 'react-redux';
import { getUsersAsAdmin } from '../administration/user-management/user-management.reducer';
import { Loading } from 'app/shared/util/loading';
import { convertDateTimeFromServer } from 'app/shared/util/date-utils';

type ISchedulingUserListModalProps = {
  // selectedTruckImplement: any[];
  // setSelectedTruckImplement: any;
  setModalOpen: any;
  props: any;
  selectedUser: any;
  setSelectedUser: any;
};

export const SchedulingUserListModal = React.memo(({ setModalOpen, selectedUser, setSelectedUser, props }: ISchedulingUserListModalProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const userList = useAppSelector(state => state.userManagement.usersCustomAdmin);
  const totalItems = useAppSelector(state => state.userManagement.totalItems);
  const loading = useAppSelector(state => state.userManagement.loading);

  const urlParams = new URLSearchParams(props.location.search);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, SMALL_ITEMS_PER_PAGE, 'licensePlate'), props.location.search)
  );
  const [searchLicensePlate, setSearchLicensePlate] = useState<string>(urlParams.get('searchLicensePlate') || '');
  const [companySearch, setCompanySearch] = useState<string>(urlParams.get('companySearch') || '');
  const [emailSearch, setEmailSearch] = useState<string>(urlParams.get('emailSearch') || '');
  const [nameSearch, setNameSearch] = useState<string>(urlParams.get('nameSearch') || '');
  const [currentSelectedUser, setCurrentSelectedUser] = useState<IUser>(selectedUser);
  const [identificationCodeSearch] = useState<string>(urlParams.get('identificationCodeSearch') || '');
  
  useEffect(() => {
    const searchParams = [];
    if (emailSearch !== '') {
      searchParams.push(`emailSearch=${encodeURIComponent(emailSearch)}`);
    }
    const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  }, [paginationState, searchLicensePlate, emailSearch, nameSearch]);

  useEffect(() => {
    getUsersFromProps();
  }, [paginationState]);

  useEffect(() => {
    if (totalItems > 0 && paginationState.activePage > Math.ceil(totalItems / paginationState.itemsPerPage)) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
    }
  }, [totalItems, paginationState.activePage, paginationState.itemsPerPage]);

  const handleSave = () => {
    setModalOpen(false);
    setSelectedUser(currentSelectedUser);
  };

  const toggleModal = () => {
    setModalOpen(prev => !prev);
  };

  const handleSelectUser = user => {
    setCurrentSelectedUser(user);
  };

  const getUsersFromProps = () => {
    dispatch(
      getUsersAsAdmin({
        page: paginationState.activePage - 1,
        size: SMALL_ITEMS_PER_PAGE,
        sort: `${paginationState.sort},${paginationState.order}`,
        emailSearch,
        companySearch,
        nameSearch,
        identificationCodeSearch,
      })
    );
  };

  const handlePagination = (_, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const TypographyTitleTable = propsTypographyForms => {
    return (
      <Typography
        {...propsTypographyForms}
        sx={{ color: 'text.primary', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
      >
        {propsTypographyForms.children}
      </Typography>
    );
  };

  const TableCellStyled = propsTableCellStyled => {
    return (
      <TableCell
        {...propsTableCellStyled}
        sx={{
          '&:hover': { color: 'secondary.contrastText' },
          borderBottom: '8px solid',
          borderColor: 'primary.main',
          display: 'table-cell',
          whiteSpace: 'nowrap',
        }}
      >
        {propsTableCellStyled.children}
      </TableCell>
    );
  };

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  const searchTypes: ISearchTypes[] = [
    {
      label: translate('userManagement.email'),
      value: emailSearch,
      placeholder: translate('userManagement.emailSearch'),
      setValue: setEmailSearch,
    },
    {
      label: translate('userManagement.firstName'),
      value: nameSearch,
      placeholder: translate('userManagement.nameSearch'),
      setValue: setNameSearch,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={true}
        onClose={toggleModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{
          boxShadow:
            'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
        }}
      >
        <DialogTitle id="dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {translate('fleetSenseApp.scheduling.choseUser')}
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" alignItems="center" justifyContent="center" mt={1} mb={2}>
            {loading && <Loading />}
            {!loading && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <PageFilters setActivePage={setActivePage} searchTypes={searchTypes} showFilters={['filters']} />
                    <TableRow>
                      <TableCell
                        sx={{
                          borderBottom: 'none',
                          paddingBottom: 1,
                        }}
                      >
                        <TypographyTitleTable>{translate('userManagement.email')}</TypographyTitleTable>
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: 'none',
                          paddingBottom: 1,
                        }}
                      >
                        <TypographyTitleTable>{translate('userManagement.firstName')}</TypographyTitleTable>
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: 'none',
                          paddingBottom: 1,
                        }}
                      >
                        <TypographyTitleTable>{translate('fleetSenseApp.userCustom.licenseValidity')}</TypographyTitleTable>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userList &&
                      userList.map((user, i) => (
                        <TableRow
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: currentSelectedUser?.id === user.id ? '' : 'grey.50' },
                            backgroundColor: currentSelectedUser?.id === user.id ? 'secondary.main' : 'background.paper',
                          }}
                          key={i}
                          onClick={() => handleSelectUser(user)}
                        >
                          <TableCellStyled>
                            <Typography
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '20ch',

                                color: currentSelectedUser?.id === user.id ? 'secondary.contrastText' : 'text.primary',
                              }}
                            >
                              {user.email}
                            </Typography>
                          </TableCellStyled>
                          <TableCellStyled>
                            <Tooltip title={user.firstName} placement="top">
                              <Typography
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '20ch',
                                  color: currentSelectedUser?.id === user.id ? 'secondary.contrastText' : 'text.primary',
                                }}
                              >
                                {user.firstName} {user.lastName}
                              </Typography>
                            </Tooltip>
                          </TableCellStyled>
                          <TableCellStyled>
                            <Typography sx={{ color: currentSelectedUser?.id === user.id ? 'secondary.contrastText' : 'text.primary' }}>
                              {user.licenseValidity ? <TextFormat type="date" value={user.licenseValidity} format={APP_LOCAL_DATE_FORMAT} /> : null}
                            </Typography>
                          </TableCellStyled>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={userList} />
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.cancel')}
          </Button>
          <Button onClick={handleSave} disableRipple variant="contained" color="secondary">
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
});
